import { useCallback, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, LogIn } from "lucide-react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  SUPPLIER_EVENT_LABELS,
  type SupplierEventInput,
  type SupplierEventKind,
  type SupplierMessageMode,
  type SupplierPurchaseOrder,
  type SupplierSignInPerson,
} from "@shared-core/data/journey-testing-types";
import { formatPreviewTime } from "@shared-core/domain/preview-clock";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import { Label } from "../../app/components/primitives/label";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "../../app/components/primitives/toggle-group";
import {
  EmptyRow,
  FailedRow,
  LoadingRow,
  describeError,
  useLoaded,
} from "./consoleState";

const EVENT_KINDS = Object.keys(SUPPLIER_EVENT_LABELS) as SupplierEventKind[];

/**
 * Suppliers (07 §1): the purchase orders scenarios raised, by supplier, and the supplier's side of each.
 * Realistic (the default) sends the supplier's message to purchasing, who record it on the real screens; Shortcut
 * records the event at once, marked simulated.
 *
 * The supplier can also be played on this preview's supplier portal: "Open as supplier" opens one order by its link
 * (as the order email would), and "Sign in as" signs this browser in as one of the supplier's users.
 */
export function SuppliersTab() {
  const adapter = useDataAdapter();
  const loadGroups = useCallback(
    () => adapter.listSupplierPurchaseOrders(),
    [adapter],
  );
  const [groups, reload] = useLoaded(loadGroups);
  const [mode, setMode] = useState<SupplierMessageMode>("realistic");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-muted-foreground">Supplier events</span>
        <ToggleGroup
          type="single"
          value={mode}
          onValueChange={(value) =>
            value && setMode(value as SupplierMessageMode)
          }
          aria-label="How a supplier event arrives"
        >
          <ToggleGroupItem value="realistic">Realistic</ToggleGroupItem>
          <ToggleGroupItem value="shortcut">Shortcut</ToggleGroupItem>
        </ToggleGroup>
        <span className="text-sm text-muted-foreground">
          {mode === "realistic"
            ? "The supplier emails purchasing; record it on the purchase order as you would."
            : "Recorded on the purchase order at once, marked simulated."}
        </span>
      </div>
      {groups.status === "loading" && (
        <LoadingRow label="Loading purchase orders…" />
      )}
      {groups.status === "failed" && (
        <FailedRow message={groups.message} onRetry={reload} />
      )}
      {groups.status === "loaded" && groups.data.length === 0 && (
        <EmptyRow>
          No purchase orders yet — run a scenario to the PO stage.
        </EmptyRow>
      )}
      {groups.status === "loaded" &&
        groups.data.map((group) => (
          <section key={group.supplier_id} className="space-y-3">
            <h2 className="text-lg font-semibold">{group.supplier_name}</h2>
            <SupplierUsers
              supplierName={group.supplier_name}
              people={group.people}
              onSignedIn={reload}
            />
            {group.purchase_orders.map((order) => (
              <PurchaseOrderCard
                key={order.purchase_order_no}
                order={order}
                mode={mode}
                onChanged={reload}
              />
            ))}
          </section>
        ))}
    </div>
  );
}

/** The supplier's users with portal access, each one click from a signed-in supplier portal. */
function SupplierUsers({
  supplierName,
  people,
  onSignedIn,
}: {
  supplierName: string;
  people: SupplierSignInPerson[];
  onSignedIn: () => void;
}) {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [signingIn, setSigningIn] = useState<string | null>(null);

  async function signInAs(person: SupplierSignInPerson) {
    setSigningIn(person.person_id);
    try {
      // Minted on click, never stored: the link works once, for 60 seconds.
      const link = await adapter.createSupplierSignInLink(person.person_id);
      capabilities.openExternalUrl(link.url);
      // A first sign-in makes an invited user active.
      if (link.is_first_sign_in) onSignedIn();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setSigningIn(null);
    }
  }

  if (people.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No one at {supplierName} has supplier portal access yet. Give someone
        access on the supplier&apos;s page, then sign in as them here.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted-foreground">
        Supplier portal users
      </span>
      {people.map((person) => (
        <Button
          key={person.person_id}
          size="sm"
          variant="outline"
          disabled={signingIn !== null}
          onClick={() => void signInAs(person)}
        >
          <LogIn className="size-4" />
          Sign in as {person.name}
          {person.state === "invited" && (
            <Badge variant="outline">invited</Badge>
          )}
        </Button>
      ))}
    </div>
  );
}

function PurchaseOrderCard({
  order,
  mode,
  onChanged,
}: {
  order: SupplierPurchaseOrder;
  mode: SupplierMessageMode;
  onChanged: () => void;
}) {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [opening, setOpening] = useState(false);

  async function openAsSupplier() {
    setOpening(true);
    try {
      const link = await adapter.createSupplierOrderLink(
        order.purchase_order_no,
      );
      capabilities.openExternalUrl(link.url);
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setOpening(false);
    }
  }
  const [kind, setKind] = useState<SupplierEventKind | null>(null);
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [reason, setReason] = useState("");
  const [quantity, setQuantity] = useState("");
  const [lineReference, setLineReference] = useState(
    order.lines[0]?.line_reference ?? "",
  );
  const [sending, setSending] = useState(false);

  async function send(chosen: SupplierEventKind) {
    const input: SupplierEventInput = { kind: chosen, mode };
    if (chosen === "despatched") {
      input.carrier = carrier || null;
      input.tracking_reference = tracking || null;
    }
    if (chosen === "cancelled" || chosen === "line_cancelled")
      input.reason = reason || null;
    if (chosen === "line_cancelled" || chosen === "short_delivered")
      input.line_references = [lineReference];
    if (chosen === "short_delivered")
      input.quantity = quantity ? Number(quantity) : null;
    setSending(true);
    try {
      await adapter.sendSupplierEvent(order.purchase_order_no, input);
      toast.success(
        mode === "realistic"
          ? `${SUPPLIER_EVENT_LABELS[chosen]}: the supplier's email is in the outbox.`
          : `${SUPPLIER_EVENT_LABELS[chosen]}: recorded on ${order.purchase_order_no}.`,
      );
      setKind(null);
      onChanged();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setSending(false);
    }
  }

  const needsDetails =
    kind === "despatched" ||
    kind === "cancelled" ||
    kind === "line_cancelled" ||
    kind === "short_delivered";

  return (
    <article
      className="space-y-3 rounded-lg border p-4"
      aria-label={`Purchase order ${order.purchase_order_no}`}
    >
      <header className="flex flex-wrap items-center gap-2">
        <span className="font-mono font-medium">{order.purchase_order_no}</span>
        <Badge variant="outline">{order.state}</Badge>
        {order.supplier_reference && (
          <span className="text-sm text-muted-foreground">
            ref {order.supplier_reference}
          </span>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto"
          disabled={opening}
          onClick={() => void openAsSupplier()}
        >
          <ExternalLink className="size-4" />
          Open as supplier
        </Button>
      </header>
      <ul className="text-sm text-muted-foreground">
        {order.lines.map((line) => (
          <li key={line.line_reference}>
            {line.line_reference} · {line.quantity} · {line.state}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        {EVENT_KINDS.map((each) => (
          <Button
            key={each}
            size="sm"
            variant={kind === each ? "default" : "outline"}
            disabled={sending}
            onClick={() =>
              each === "placed" || each === "delivered"
                ? void send(each)
                : setKind(each)
            }
          >
            {SUPPLIER_EVENT_LABELS[each]}
          </Button>
        ))}
      </div>
      {needsDetails && kind && (
        <div className="flex flex-wrap items-end gap-3 rounded-md bg-muted/40 p-3">
          {kind === "despatched" && (
            <>
              <Field
                id={`${order.purchase_order_no}-carrier`}
                label="Carrier"
                value={carrier}
                onChange={setCarrier}
              />
              <Field
                id={`${order.purchase_order_no}-tracking`}
                label="Tracking"
                value={tracking}
                onChange={setTracking}
              />
            </>
          )}
          {(kind === "line_cancelled" || kind === "short_delivered") && (
            <Field
              id={`${order.purchase_order_no}-line`}
              label="Line"
              value={lineReference}
              onChange={setLineReference}
            />
          )}
          {kind === "short_delivered" && (
            <Field
              id={`${order.purchase_order_no}-quantity`}
              label="Quantity delivered"
              value={quantity}
              onChange={setQuantity}
            />
          )}
          {(kind === "cancelled" || kind === "line_cancelled") && (
            <Field
              id={`${order.purchase_order_no}-reason`}
              label="Reason"
              value={reason}
              onChange={setReason}
            />
          )}
          <Button size="sm" disabled={sending} onClick={() => void send(kind)}>
            Send {SUPPLIER_EVENT_LABELS[kind].toLowerCase()}
          </Button>
        </div>
      )}
      {order.messages.length > 0 && (
        <ul className="space-y-1 text-sm">
          {order.messages.map((message) => (
            <li key={message.id} className="flex flex-wrap gap-2">
              <span>{SUPPLIER_EVENT_LABELS[message.kind]}</span>
              <Badge variant="outline">{message.mode}</Badge>
              <span className="text-muted-foreground">
                {formatPreviewTime(message.created_at)}
              </span>
              <span
                className={
                  message.recorded_at ? "text-emerald-600" : "text-amber-600"
                }
              >
                {message.recorded_at
                  ? "recorded by purchasing"
                  : "waiting for purchasing"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="grid gap-1">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <Input
        id={id}
        className="h-8 w-44"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
